import { auth } from 'data/firestore/firebase';
import getUser from 'data/firestore/user/get';
import { invoiceBuilderRequest } from './api/client';
import { INVOICE_ACTIVITY_PAGE_SIZE } from './constants';
import { invoiceActivitiesPath } from './helpers';
import { activityActorName } from './invoiceActivity';
import { isPrestigeInvoiceAgent } from './InvoicePdf/prestige';
import { Invoice, InvoiceActivityListEnvelope } from './types';

const names = new Map<string, Promise<string>>();

const readableName = (displayName: string, uid = '') => {
    const name = activityActorName({ displayName, uid });
    return name === 'Unknown' ? '' : name;
};

const creatorFromActivities = async (
    organizationId: string,
    invoiceId: string,
    cursor?: string,
    page = 0,
): Promise<string> => {
    if (page >= 5) return '';
    const envelope = await invoiceBuilderRequest<InvoiceActivityListEnvelope>(
        'GET',
        invoiceActivitiesPath(organizationId, invoiceId, {
            cursor,
            limit: INVOICE_ACTIVITY_PAGE_SIZE,
        }),
    );
    const created = envelope.data.activities.find((item) => item.kind === 'invoice.created');
    if (created) return readableName(created.actor.displayName, created.actor.uid);
    const next = envelope.data.nextCursor || '';
    if (!next) return '';
    return creatorFromActivities(organizationId, invoiceId, next, page + 1);
};

const currentUserDisplayName = async () => {
    const current = auth.currentUser;
    const uid = current?.uid || '';
    const authName = current?.displayName || '';
    if (!uid) return '';
    const user = await getUser(uid);
    return readableName(user?.displayName || authName, uid);
};

const loadCreatorName = (organizationId: string, invoice: Invoice) => (
    invoice.id
        ? creatorFromActivities(organizationId, invoice.id)
        : currentUserDisplayName()
);

export const invoiceWithCreator = async (
    invoice: Invoice,
    agentId: string,
): Promise<Invoice> => {
    if (!isPrestigeInvoiceAgent(agentId)) return invoice;
    const existing = invoice.createdBy?.displayName?.trim() || '';
    if (existing) return invoice;

    const key = invoice.id
        ? `${agentId}/${invoice.id}`
        : `draft/${auth.currentUser?.uid || ''}`;
    let pending = names.get(key);
    if (!pending) {
        pending = (async () => {
            try {
                const name = await loadCreatorName(agentId, invoice);
                if (!name) names.delete(key);
                return name;
            } catch {
                names.delete(key);
                return '';
            }
        })();
        names.set(key, pending);
    }
    const displayName = await pending;
    if (!displayName) return invoice;
    return {
        ...invoice,
        createdBy: { displayName },
    };
};
